import { useEffect } from 'react';

import {
	ANALYTICS_OPERATIONAL_ACTION,
	ANALYTICS_OPERATIONAL_SUBJECT,
	ANALYTICS_UI_SUBJECT,
} from '../../constants/snippetAnalytics.ts';
import {
	setOnFetchComplete,
	setOnFetchScopeError,
	setOnFetchScopeSuccess,
	type FetchPerformanceMetrics,
	type FetchScopeErrorInfo,
	type FetchScopeSuccessInfo,
} from '../../store/actions/fetchActions.ts';

import { useSnippetAnalytics } from './useSnippetAnalytics.ts';

/**
 * Registers analytics callbacks for snippet fetch performance metrics and
 * per-scope fetch successes/failures.
 *
 * On successful fetch: fires a FETCHED operational event with performance metrics
 * (snippetCount, userScopeCount, globalScopeCount, payloadSizeBytes, durationMs).
 *
 * On per-scope failure: fires a `snippetApiFetch FAILED` operational event with
 * the scope and (when available) a `statusCode`. This base failure event fires
 * unconditionally so the existing fetch-failure signal is never lost.
 *
 * The failure event also carries a categorical `reason` attribute, making fetch
 * failures diagnosable in the reliability dashboard. A per-scope
 * `snippetApiFetch SUCCEEDED` operational event is fired as well, so a genuine
 * fetch error rate = failures / (successes + failures) can be computed at the
 * same granularity as the failure event.
 *
 * The raw error message is intentionally never emitted, to avoid leaking server
 * response bodies (potential PII/UGC) into analytics.
 *
 * Call this from a component that is mounted before fetchSnippets() runs
 * (e.g. SnippetsProvider).
 */
export const useSnippetsFetchAnalytics = (): void => {
	const { fireOperationalAnalytic, fireFailedAnalytic, fireSuccessAnalytic } =
		useSnippetAnalytics();

	useEffect(() => {
		setOnFetchComplete((metrics: FetchPerformanceMetrics) => {
			fireOperationalAnalytic({
				actionSubject: ANALYTICS_UI_SUBJECT.SNIPPETS,
				action: ANALYTICS_OPERATIONAL_ACTION.FETCHED,
				attributes: metrics,
			});
		});

		setOnFetchScopeSuccess((info: FetchScopeSuccessInfo) => {
			fireSuccessAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_FETCH, {
				scope: info.scope,
			});
		});

		setOnFetchScopeError((info: FetchScopeErrorInfo) => {
			fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_FETCH, {
				scope: info.scope,
				...(info.reason !== undefined && { reason: info.reason }),
				...(info.statusCode !== undefined && { statusCode: info.statusCode }),
			});
		});

		return () => {
			setOnFetchComplete(null);
			setOnFetchScopeSuccess(null);
			setOnFetchScopeError(null);
		};
		// `fireOperationalAnalytic`, `fireFailedAnalytic` and `fireSuccessAnalytic`
		// are memoised in useSnippetAnalytics, so they are referentially stable and
		// safe to list here.
	}, [fireOperationalAnalytic, fireFailedAnalytic, fireSuccessAnalytic]);
};
